import { useContext, useMemo } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import { PanelMenu } from "primereact/panelmenu"
import useMainMenu from "../../hooks/useMainMenu"
import useTemplateAvailability from "../../hooks/useTemplateAvailability"
import { LoaderMedium } from "../Loaders"
import { UserContext } from "../../context/userContext"
import {
  buildPanelMenuModel,
  renderMaterialIconMenuItemTemplate,
} from "./components/helpers/menuBuilder"

import baseStyles from "../../styles/SidebarPanelMenuBase.module.css"

function ActivityLogSidebar() {
  const navigate = useNavigate()
  const location = useLocation()
  const userCtx = useContext(UserContext)

  // Dependencies for reloading template availability based on user session changes.
  const templatesReloadDeps = useMemo(
    () => [userCtx.session.menu, userCtx.session.ux],
    [userCtx.session.menu, userCtx.session.ux],
  )

  const { customTemplates, loadingTemplates, isTemplateAvailable } =
    useTemplateAvailability({
      reloadDeps: templatesReloadDeps,
    })

  const {
    menuItems: activityLogMenuSections,
    loading: loadingActivityLogMenu,
    resolveMenuPath,
  } = useMainMenu({
    // Pull only the Activity Logs parent section from main menu config.
    includeLabels: ["Activity Logs"],
  })

  const activityLogChildren = Array.isArray(activityLogMenuSections?.[0]?.items)
    ? activityLogMenuSections[0].items
    : []

  const configuredActivityLogItems = activityLogChildren
    .filter(
      (item) =>
        Boolean(item?.label) &&
        // Config menu items map availability by template title.
        isTemplateAvailable({ template_title: item.label }) &&
        typeof item?.path === "string" &&
        item.path.trim().length > 0,
    )
    .map((item) => ({
      ...item,
      path: resolveMenuPath(item.path),
    }))

  const customActivityLogItems = customTemplates
    // Custom activity templates are appended like NavMain's activity_custom entries.
    .filter((item) => item.template_category === "activity_custom")
    .filter((item) => isTemplateAvailable({ template_id: item.template_id }))
    .map((item) => ({
      label: item.template_title,
      path: `/activity-log/${item.template_id}/`,
      icon: "book",
    }))

  const activityLogItems = [...configuredActivityLogItems, ...customActivityLogItems]

  const activityLogMenuModel = buildPanelMenuModel({
    items: activityLogItems,
    pathname: location.pathname,
    navigate,
    itemTemplate: renderMaterialIconMenuItemTemplate,
    activeClassName: baseStyles.menuLinkActiveItem,
  })

  if (loadingActivityLogMenu || loadingTemplates) {
    // Avoid rendering partial menu state while any required source is still loading.
    return (
      <div className={baseStyles.menuRoot}>
        <LoaderMedium />
      </div>
    )
  }

  if (!activityLogMenuModel.length) return null

  return (
    <div className={baseStyles.menuRoot}>
      <PanelMenu
        key={`activity-log-menu-${location.pathname}`}
        model={activityLogMenuModel}
        multiple={false}
      />
    </div>
  )
}

export default ActivityLogSidebar
